import { useEffect, useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import NotificationDropdown from '../feature/NotificationDropdown';
import LiveChat from '../feature/LiveChat';
import { useAuth } from '../../auth/AuthContext';
import { useSiteSettings } from '@/settings/useSiteSettings';

interface DashboardLayoutProps {
  mode: 'customer' | 'admin';
}

export default function DashboardLayout({ mode }: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, role, user, signOut } = useAuth();
  const { settings } = useSiteSettings();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const nav = (mode === 'admin' ? settings.dashboard.adminMenu : settings.dashboard.customerMenu)
    .filter((item) => item.enabled);
  const sidebarTitle = mode === 'admin' ? settings.dashboard.adminSidebarTitle : settings.dashboard.customerSidebarTitle;
  const topbarTitle = mode === 'admin' ? settings.dashboard.adminTopbarTitle : settings.dashboard.customerTopbarTitle;
  const logoUrl = settings.general.dashboardLogoUrl || settings.general.logoUrl;
  const displayName = profile?.name || user?.email || 'Account';
  const initials = displayName.charAt(0).toUpperCase();
  // Auto-dismiss welcome back after 6 seconds
  const { welcomeBack, clearWelcomeBack } = useAuth();
  const [showWelcome, setShowWelcome] = useState(welcomeBack);

  useEffect(() => {
    if (welcomeBack) {
      setShowWelcome(true);
      const timer = setTimeout(() => {
        setShowWelcome(false);
        clearWelcomeBack();
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [welcomeBack, clearWelcomeBack]);

  const isActive = (href: string) => {
    if (href === '/dashboard' || href === '/admin') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-surface-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-surface-900 text-surface-300 flex flex-col transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
        <div className="p-5 flex items-center gap-2.5 border-b border-surface-800">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={settings.general.storeName}
              className="w-7 h-7 rounded object-contain"
            />
          ) : (
            <div className="w-7 h-7 rounded bg-brand-600 text-white flex items-center justify-center text-xs font-bold">
              {settings.general.shortName.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <span className="font-semibold text-white text-sm block leading-tight">
              {sidebarTitle}
            </span>
            <span className="text-[10px] text-surface-500 font-medium">
              v-4.3
            </span>
          </div>
        </div>

        {settings.dashboard.showVisitShop && (
          <div className="px-3 pt-3">
            <Link
              to="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-surface-300 hover:text-white bg-surface-800 hover:bg-surface-700 transition-colors">
              <i className="ri-store-2-line text-base" />
              Visit Shop
              <i className="ri-external-link-line text-xs ml-auto opacity-60" />
            </Link>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {nav.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? 'bg-brand-600 text-white'
                  : 'text-surface-400 hover:text-white hover:bg-surface-800'
              }`}>
              <i className={`${item.icon} text-base`} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="p-3 border-t border-surface-800">
          <button
            onClick={async () => {
              await signOut();
              navigate('/login', { replace: true });
            }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-surface-400 hover:text-white hover:bg-surface-800 transition-colors w-full">
            <i className="ri-logout-box-r-line text-base" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-surface-200 px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-600">
              <i className="ri-menu-line" />
            </button>
            <div className="text-sm text-surface-500 hidden sm:block">
              {topbarTitle}
              {role && (
                <span className="ml-2 text-xs text-surface-400">({role})</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationDropdown mode={mode} />
            <button
              onClick={() =>
                navigate(
                  mode === 'admin' ? '/admin/profile' : '/dashboard/profile',
                )
              }
              className="flex items-center gap-2">
              {profile?.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt={displayName}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <span className="w-8 h-8 rounded-full bg-brand-600 text-white text-sm font-semibold flex items-center justify-center">
                  {initials}
                </span>
              )}
              <span className="text-sm font-medium text-surface-700 hidden sm:block">
                {displayName}
              </span>
            </button>
          </div>
        </header>

        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {/* Welcome Back popup */}
      {showWelcome && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          <div className="bg-white rounded-xl border border-green-200 shadow-xl p-5 max-w-sm flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <i className="ri-hand-wave-line text-green-600 text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-surface-900">Welcome Back!</h4>
              <p className="text-xs text-surface-500 mt-0.5 leading-relaxed">
                Your account has been reactivated. Your licenses are ready to use.
              </p>
            </div>
            <button
              onClick={() => { setShowWelcome(false); clearWelcomeBack(); }}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-surface-100 text-surface-400 shrink-0">
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>
      )}

      {/* Live Chat widget */}
      {role !== 'admin' && <LiveChat />}
    </div>
  );
}
